/**
 * Утилита для работы с контактными параметрами из URL и iframe data-атрибутов
 * Позволяет передавать контактную информацию (телефон, email, локации) через URL параметры iframe
 */

import { getUrlParam } from './urlParams.js';

/**
 * Маппинг URL параметров на ключи контактной информации
 */
const CONTACT_PARAM_MAPPING = {
  // Basic contact details
  contactPhone: 'contactPhone',
  contactEmail: 'contactEmail',
  contactWhatsapp: 'contactWhatsapp',
  // Хендл Telegram-бота дилера (#1336): `name`, `@name` или `t.me/name`
  contactTelegram: 'contactTelegram',

  // Business hours (per-day)
  hoursMon: 'hoursMon',
  hoursTue: 'hoursTue',
  hoursWed: 'hoursWed',
  hoursThu: 'hoursThu',
  hoursFri: 'hoursFri',
  hoursSat: 'hoursSat',
  hoursSun: 'hoursSun',

  // Location 1
  location1Name: 'location1Name',
  location1Address: 'location1Address',
  location1City: 'location1City',
  location1Country: 'location1Country',
  location1Maps: 'location1Maps',

  // Location 2 (optional)
  location2Name: 'location2Name',
  location2Address: 'location2Address',
  location2City: 'location2City',
  location2Country: 'location2Country',
  location2Maps: 'location2Maps',

  // Company info
  companyName: 'companyName',
  companyDescription: 'companyDescription',
};

/**
 * Получить контактную информацию из URL параметров
 * @returns {Object} Объект с контактными параметрами
 */
export function getContactFromUrl() {
  const contactParams = {};

  Object.keys(CONTACT_PARAM_MAPPING).forEach(paramName => {
    const value = getUrlParam(paramName);
    if (value) {
      const key = CONTACT_PARAM_MAPPING[paramName];
      contactParams[key] = value;
      console.log(`[contactParams] Found URL parameter: ${paramName} = ${value}`);
    }
  });

  if (Object.keys(contactParams).length > 0) {
    console.log('[contactParams] Contact parameters from URL:', contactParams);
  }

  return contactParams;
}

/**
 * Получить контактную информацию из data-атрибутов родительского iframe
 * Работает только для same-origin embedding
 * @returns {Object} Объект с контактными параметрами
 */
export function getContactFromParentIframe() {
  const contactParams = {};

  try {
    // Проверяем, находимся ли мы в iframe
    if (window.self === window.top) {
      return contactParams;
    }

    // Пытаемся получить доступ к родительскому окну
    const parentIframe = window.frameElement;

    if (!parentIframe) {
      return contactParams;
    }

    // Маппинг data-атрибутов в kebab-case на camelCase ключи
    const dataAttrMapping = {
      'contact-phone': 'contactPhone',
      'contact-email': 'contactEmail',
      'contact-whatsapp': 'contactWhatsapp',
      'contact-telegram': 'contactTelegram',
      'hours-mon': 'hoursMon',
      'hours-tue': 'hoursTue',
      'hours-wed': 'hoursWed',
      'hours-thu': 'hoursThu',
      'hours-fri': 'hoursFri',
      'hours-sat': 'hoursSat',
      'hours-sun': 'hoursSun',
      'location1-name': 'location1Name',
      'location1-address': 'location1Address',
      'location1-city': 'location1City',
      'location1-country': 'location1Country',
      'location1-maps': 'location1Maps',
      'location2-name': 'location2Name',
      'location2-address': 'location2Address',
      'location2-city': 'location2City',
      'location2-country': 'location2Country',
      'location2-maps': 'location2Maps',
      'company-name': 'companyName',
      'company-description': 'companyDescription',
    };

    // Читаем все data-атрибуты iframe
    Object.keys(dataAttrMapping).forEach(dataAttr => {
      const value = parentIframe.getAttribute(`data-${dataAttr}`);
      if (value) {
        const key = dataAttrMapping[dataAttr];
        contactParams[key] = value;
        console.log(`[contactParams] Found iframe data-attribute: data-${dataAttr} = ${value}`);
      }
    });

    if (Object.keys(contactParams).length > 0) {
      console.log('[contactParams] Contact parameters from iframe data-attributes:', contactParams);
    }
  } catch (e) {
    // Cross-origin restriction - не можем получить доступ к родительскому iframe
    console.log('[contactParams] Cannot access parent iframe (cross-origin), skipping data-attributes');
  }

  return contactParams;
}

/**
 * Получить контактную информацию из window.runtimeConfig и import.meta.env
 * Используется когда виджет монтируется через B2BDemo.mount() с contactInfo в config
 * или когда переменные заданы в .env файлах
 * @returns {Object} Объект с контактными параметрами
 */
export function getContactFromRuntimeConfig() {
  const contactParams = {};

  try {
    const config = window.runtimeConfig || {};

    // Маппинг runtimeConfig ключей на контактные параметры
    const runtimeConfigMapping = {
      VITE_CONTACT_PHONE: 'contactPhone',
      VITE_CONTACT_EMAIL: 'contactEmail',
      VITE_CONTACT_WHATSAPP: 'contactWhatsapp',
      VITE_CONTACT_TELEGRAM: 'contactTelegram',
      VITE_HOURS_MON: 'hoursMon',
      VITE_HOURS_TUE: 'hoursTue',
      VITE_HOURS_WED: 'hoursWed',
      VITE_HOURS_THU: 'hoursThu',
      VITE_HOURS_FRI: 'hoursFri',
      VITE_HOURS_SAT: 'hoursSat',
      VITE_HOURS_SUN: 'hoursSun',
      VITE_LOCATION1_NAME: 'location1Name',
      VITE_LOCATION1_ADDRESS: 'location1Address',
      VITE_LOCATION1_CITY: 'location1City',
      VITE_LOCATION1_COUNTRY: 'location1Country',
      VITE_LOCATION1_MAPS: 'location1Maps',
      VITE_LOCATION2_NAME: 'location2Name',
      VITE_LOCATION2_ADDRESS: 'location2Address',
      VITE_LOCATION2_CITY: 'location2City',
      VITE_LOCATION2_COUNTRY: 'location2Country',
      VITE_LOCATION2_MAPS: 'location2Maps',
      VITE_COMPANY_NAME: 'companyName',
      VITE_COMPANY_DESCRIPTION: 'companyDescription',
    };

    Object.entries(runtimeConfigMapping).forEach(([configKey, paramKey]) => {
      // Приоритет: window.runtimeConfig > import.meta.env
      const value = config[configKey] || import.meta.env[configKey];
      if (value) {
        contactParams[paramKey] = value;
      }
    });

    if (Object.keys(contactParams).length > 0) {
      console.log('[contactParams] Contact parameters from runtimeConfig/env:', contactParams);
    }
  } catch (e) {
    console.warn('[contactParams] Error reading runtimeConfig:', e.message);
  }

  return contactParams;
}

/**
 * Получить полную контактную информацию из всех источников
 * Приоритет: URL параметры > iframe data-атрибуты > runtimeConfig
 * @returns {Object} Объект с контактными параметрами
 */
export function getContactParams() {
  // Получаем параметры из всех источников (от низкого к высокому приоритету)
  const runtimeConfigParams = getContactFromRuntimeConfig();
  const iframeParams = getContactFromParentIframe();
  const urlParams = getContactFromUrl();

  return {
    ...runtimeConfigParams, // Lowest priority: runtimeConfig (from widget mount config)
    ...iframeParams,        // Medium priority: iframe data-атрибуты
    ...urlParams,           // Highest priority: URL параметры
  };
}
